/**
 * TutorialTree — grouped tutorial list for the Tutorials page.
 *
 * Props:
 *   highlightId  {string}   — tutorial id to auto-scroll and highlight
 *   onPlay       {fn}       — called with tutorial object when Watch is clicked
 *
 * Data shape: a node is either a leaf ({ items: [...] }) or a branch
 * ({ children: [...nodes] }) — arbitrary depth, rendered recursively.
 */

import React, { useRef, useEffect, useState } from "react";

// Runtime white-label config — set by nginx-mounted /app-config.js (see BrandingSettingsPage)
const appName = (window.APP_CONFIG || {}).appName || "Fincrest Systems";

/* ── Tutorial data — 6 top-level sections, organised by strategy ─────────── */
export const TUTORIALS = [
  /* ─────────────────────────────────────────────────────────────────────
   * SECTION 1 — THE PROBLEM
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "The Problem",
    color: "#e74c3c",
    icon: "⚠️",
    description: "Start here. Understand why manual options management at scale breaks down — before we show you anything else.",
    items: [
      {
        id: "P01", title: "The Multi-Account Problem", duration: "90s", priority: "P0",
        desc: "Managing options manually across multiple accounts is a scaling problem. See exactly where it breaks.",
        topics: [
          "Why manual management doesn't scale beyond one account",
          "The consistency gap: same stock, different decisions across accounts",
          "What rule-based automation actually solves at scale",
        ],
        videoSrc: "/tutorials/platform/p01-multi-account.mp4",
      },
      {
        id: "P02", title: "Why Manual Options Management Fails", duration: "90s", priority: "P0",
        desc: "Even for a single account, manual options management has a structural inconsistency problem. Here's the evidence.",
        topics: [
          "Decision fatigue at expiry — why the last week is the hardest",
          "Inconsistent roll timing and what it costs over 12 months",
          "The compounding cost of reactive management vs systematic rules",
        ],
        videoSrc: "/tutorials/platform/p02-manual-options.mp4",
      },
      {
        id: "T01", title: "Trust & Control", duration: "60s", priority: "P0",
        desc: "The system never places an order without your explicit approval. See exactly how the human-in-the-loop approval flow works — from rule fire to executed trade.",
        topics: [
          "Human-in-the-loop design: why every suggestion requires explicit approval before any order is placed",
          "How to approve, reject, or snooze any suggestion — and what each choice means",
          "Full audit trail: every decision, every order, every outcome recorded permanently",
        ],
        videoSrc: "/tutorials/platform/t01-approval.mp4",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * SECTION 2 — GETTING STARTED
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Getting Started",
    color: "#545cd8",
    icon: "🚀",
    description: "New to the platform? These two videos get you oriented before you build your first strategy.",
    items: [
      {
        id: "00", title: `Welcome to ${appName}`, duration: "52s", priority: "P0",
        desc: `A quick overview of what ${appName} does, the two strategies it supports, and why rule-based automation changes the options trading game.`,
        topics: [
          `What ${appName} automates: rule-based options management for Pyramid Hedging`,
          "The rule engine cycle: how it evaluates your rules every 5 minutes and queues suggestions",
          "Human-in-the-loop: every suggestion requires your approval before any order executes",
        ],
        videoSrc: "/tutorials/platform/00-welcome.mp4",
      },
      {
        id: "01", title: "Dashboard Walkthrough", duration: "90s", priority: "P0",
        desc: "A full tour of the main dashboard: P&L cards, strategy status, position summary, and how to navigate between the platform's core pages.",
        topics: [
          "Total P&L, stock P&L, option P&L, and potential P&L — what each card means",
          "Active strategies and active rules badges: what triggers a number change",
          "Short option value and open positions at a glance",
          "Quick-action buttons: jump directly to suggestions, rules, positions, or strategy builder",
        ],
        videoSrc: "/tutorials/platform/01-dashboard.mp4",
      },
      {
        id: "01B", title: "Connecting Your Brokerage", duration: "2:21 min", priority: "P0",
        desc: "The platform can't do anything on your behalf until it can see your brokerage — positions, fills, balances. A single SnapTrade OAuth connection covers 35+ brokerages; you never type a credential into the platform itself.",
        topics: [
          "Why a connected brokerage is required before any strategy can be built or activated",
          "One login covers 35+ brokerages — Alpaca, Schwab, Fidelity, E-Trade, Robinhood, and more",
          "The SnapTrade OAuth flow: you authorize through your broker's own login, not ours",
          "Reading connection status: Read-only vs. Trading Enabled authorization badges",
        ],
        videoSrc: "/tutorials/platform/01b-brokerage.mp4",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * SECTION 3 — RULES
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Rules",
    color: "#f7b924",
    icon: "⚙️",
    description: `The rule engine is what makes ${appName} systematic rather than reactive. These videos explain how it works and how to configure it for your strategy.`,
    items: [
      {
        id: "03", title: "Understanding the Rule Engine", duration: "2:39 min", priority: "P0",
        desc: "The conceptual foundation: how facts, conditions, and actions work together to drive every automated decision the system makes — walked through with real Pyramid Hedging rules.",
        topics: [
          "Facts: ltp, next_buy_price, stock_pct_drop, hedge_pct — recalculated every 5-minute cycle",
          "Conditions: AND/OR logic trees that evaluate facts against thresholds",
          "Actions: what the engine suggests — BUY_STOCK, ROLL_DOWN_CALL, ROLL_OUT_CALL, and more",
          "Groups and priority: how the engine picks one winner when multiple rules trigger together",
          "Cooldowns and group_max_fires: preventing queue flooding on rapid price moves",
        ],
        videoSrc: "/tutorials/platform/03-rules.mp4",
      },
      {
        id: "04", title: "Creating Rules from Templates", duration: "2 min", priority: "P1",
        desc: "Import and activate the pre-built rule templates for your strategy in under 2 minutes — the fastest way to get all 11 PH scenarios covered from day one.",
        topics: [
          "The default Pyramid Hedging template: all 11 rule scenarios pre-configured",
          "Rule groups explained: Roll Management, Income First Mode, Exit Options, Expiry Management",
          "How to activate a template and verify all rules are in ACTIVE state",
          "Priority order: why some rules must rank higher than others",
        ],
        videoSrc: "/tutorials/platform/04-templates.mp4",
      },
      {
        id: "05", title: "Building Custom Rules", duration: "2.5 min", priority: "P1",
        desc: "Build advanced IF/THEN rules from scratch using the visual rule editor — custom conditions, groups, cooldowns, and action parameters.",
        topics: [
          "Adding conditions: picking a fact, operator, and threshold value",
          "AND/OR logic: combining multiple conditions for precise triggers",
          "Setting action type and action-specific parameters",
          "Group, priority, and cooldown configuration — and why cooldowns matter for stability",
          "Saving and validating: how to confirm a rule will fire as expected",
        ],
        videoSrc: null,
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * SECTION 4 — MONITORING & EXECUTION
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Monitoring & Execution",
    color: "#30b1ff",
    icon: "👁️",
    description: "Your day-to-day workflow: reading live positions, approving suggestions, and staying informed with price alerts.",
    items: [
      {
        id: "06", title: "Monitoring Positions & Greeks", duration: "2:23 min", priority: "P1",
        desc: "How to read your live option positions table, understand the delta/theta/IV columns, and track unrealised vs potential P&L in real time.",
        topics: [
          "Options positions table: what each column means (delta, theta, IV, unrealised, ITM indicator)",
          "How delta changes as the stock moves and what it means for your hedge percentage",
          "Unrealised P&L vs potential P&L: how they differ and which one matters for the rule engine",
          "Using the strategy summary view to see all active positions at a glance",
        ],
        videoSrc: "/tutorials/platform/06-monitoring.mp4",
      },
      {
        id: "07", title: "The Automation Approval Flow", duration: "2 min", priority: "P0",
        desc: "How rule-generated suggestions become executed trades — the full flow from rule fire through suggestion card to filled order.",
        topics: [
          "Rule fire → suggestion card: what triggers a card to appear and what it shows",
          "Approving a suggestion: what happens next and how long order execution takes",
          "Snooze vs reject: how each affects the cooldown and when you'd choose each",
          "Order queue: tracking pending, executing, and filled orders",
          "Execution history: the permanent audit trail for every decision",
        ],
        videoSrc: "/tutorials/platform/07-approval.mp4",
      },
      {
        id: "08", title: "Setting Up Price Alerts", duration: "90s", priority: "P2",
        desc: "Configure Telegram and email price threshold alerts on any tracked symbol so you're notified the moment a stock hits a level you care about.",
        topics: [
          "Adding a price alert: choosing a symbol, direction (above/below), and threshold",
          "Telegram and email notification channels: how to connect and test each",
          "How alerts interact with the rule engine — alerts notify, rules act",
          "Alert history: reviewing past firings and adjusting thresholds",
        ],
        videoSrc: "/tutorials/platform/08-alerts.mp4",
      },
      {
        id: "08B", title: "Notification Settings", duration: "1:38 min", priority: "P1",
        desc: "Price Alerts fire on a symbol crossing a threshold you set. Notification Settings is different — it controls how the platform reaches you when the system itself does something: a new suggestion, an order fill, an order failure, or a critical error.",
        topics: [
          "Two channels: Email and Telegram, each with its own toggle and test button",
          "Email: enter your address, click Send Test Email for instant confirmation",
          "Telegram: message @userinfobot to get your numeric Chat ID, paste it in, then Send Test Message",
          "Why this is a separate page from Price Alerts — system events vs. price thresholds",
        ],
        videoSrc: "/tutorials/platform/10-notifications.mp4",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * SECTION 5 — STRATEGIES (multi-level)
   * Every strategy on the platform except DHSS lives under this one
   * expandable parent. Each strategy in turn breaks into its own named
   * sub-sections (Strategy Guide / Comparisons / Trading Examples /
   * Backtests) — only Pyramid Hedging has a Comparisons bucket; the
   * others fold their comparison-style videos into Strategy Guide.
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Strategies",
    color: "#6366f1",
    icon: "🧩",
    description: "Pick a strategy to see its full guide — philosophy, comparisons, every setting, and real backtest proof.",
    children: [
      /* ══════════════════════ PYRAMID HEDGING ══════════════════════ */
      {
        series: "Pyramid Hedging",
        color: "#7c3aed",
        icon: "📈",
        description: "The complete guide to Pyramid Hedging — why it works, how it compares to strategies you may already run, how to configure every setting, and real backtest proof.",
        children: [
          {
            series: "Strategy Guide",
            color: "#7c3aed",
            icon: "📘",
            description: "Start here: how to build a strategy, the core Pyramid Hedging concept, the settings that drive it, and the philosophy behind buying up instead of down.",
            items: [
              {
                id: "02", title: "Building Your First Strategy", duration: "4:47 min", priority: "P0",
                desc: "A step-by-step walkthrough of the Strategy Builder UI — from picking a symbol and expiry to reading the output and activating your strategy.",
                topics: [
                  "Configuring the ladder: step gap, position size cap, hedge style, and the Cash-Secured Put leg",
                  "Adding a ticker, setting the expiry date, and walking through each settings field",
                  "Reading the builder output: what the ladder preview, call strike, and projected income show",
                  "Saving vs activating — what each does and when you'd do one without the other",
                  "What happens in the first 5-minute cycle after activation",
                ],
                videoSrc: "/tutorials/platform/02-strategy.mp4",
              },
              {
                id: "C00", title: "Pyramid Hedging: The Swiss-Army Strategy", duration: "2 min", priority: "P0",
                desc: "One engine, four configurations. This video maps Pyramid Hedging's setup options onto the covered call, the Wheel, and even the short strangle — and previews the comparisons ahead.",
                topics: [
                  "Base configuration (ladder + single call hedge) ≈ a modified covered call",
                  "+ Cash-Secured Put ≈ the Wheel, with two income engines from day one",
                  "+ CSP + Income-First Mode ≈ a short strangle — same income objective, plus a profit floor",
                  "A roadmap to the comparison videos that follow",
                ],
                videoSrc: "/tutorials/pyramid_hedging/c00-swiss-army.mp4",
              },
              {
                id: "C06", title: "Inside Income-First Mode: How Pyramid Hedging Out-Engineers the Strangle", duration: "4 min", priority: "P1",
                desc: "A deep dive into the mechanics behind the strangle comparison's claim: how the ladder is sized to guarantee a profit floor on the upside, and how automated delta-hedge rolls defend against losses on the downside — including the worst case where the CSP goes in-the-money.",
                topics: [
                  "Tail P&L: the one number that drives ladder size — stock gain + call premium + put premium at full-ladder completion",
                  "How Income-First Mode searches for the smallest ladder that clears the Minimum Cycle Profit Target",
                  "Once the ladder completes, the position becomes a covered call — the profit floor is locked in regardless of how high the stock goes",
                  "delta_difference and ROLL_DOWN_CALL: automated delta-neutral rebalancing every 5-minute cycle",
                  "The ITM-put rescue: rolling the CSP toward ATM to restore the profit floor",
                ],
                videoSrc: "/tutorials/pyramid_hedging/c06-income-first-mode.mp4",
              },
              {
                id: "G01", title: "Why We Buy Up, Not Down", duration: "114s", priority: "P0",
                desc: "Every investing instinct says buy the dip. This video makes the structural case for why buying on confirmed upward momentum — not on falling prices — is fundamentally different from cost-averaging.",
                topics: [
                  "Momentum confirmation vs hope-based buying: the key structural difference",
                  "How upward ladder buys reinforce the call hedge naturally as the position grows",
                  "The pause: what happens when the stock stalls between rungs",
                  "Why Pyramid Hedging never averages down — and why that matters for risk",
                ],
                videoSrc: "/tutorials/pyramid_hedging/g01-why-we-buy-up.mp4",
              },
              {
                id: "PH-S", title: "Pyramid Hedging: Settings Guide", duration: "~9 min", priority: "P1",
                desc: "A complete walkthrough of every Pyramid Hedging setting, organized as 4 chapters. By the end you'll understand the maths behind the ladder, know how to position your hedge for bullish or income-focused setups, and have both income engines — call and CSP — working for you.",
                topics: [
                  "Chapter 1 — The Ladder: step gap floor, budget cap, and the effective average cost formula with worked NVDA example",
                  "Chapter 2 — Hedge Style: target_strike = projected_avg ÷ slider; 0.90 (bullish, OTM call) vs 1.10 (income, tight call)",
                  "Chapter 3 — Income-First Mode: flip the optimizer to hit your monthly income target before expanding the ladder",
                  "Chapter 4 — The CSP: safety buffer, rollup trigger, and why disabling it removes ~40% of total strategy income",
                ],
                videoSrc: "/tutorials/pyramid_hedging/ph_settings_guide.mp4",
              },
            ],
          },
          {
            series: "Comparisons",
            color: "#7c3aed",
            icon: "⚖️",
            description: "How Pyramid Hedging stacks up against strategies you may already run — same market exposure, different mechanics.",
            items: [
              {
                id: "C01", title: "Covered Call vs. Pyramid Hedging", duration: "105s", priority: "P1",
                desc: "The classic covered call collects premium but caps upside permanently. PH adapts the hedge as the position grows.",
                topics: [
                  "Static vs adaptive call placement — what changes as the stock rises",
                  "What happens when the stock rallies past a fixed call strike",
                  "How PH collects premium without a permanent upside ceiling",
                ],
                videoSrc: "/tutorials/pyramid_hedging/c01-covered-call.mp4",
              },
              {
                id: "C02", title: "Cash-Secured Put vs. Pyramid Hedging", duration: "105s", priority: "P1",
                desc: "CSPs buy on assignment — catching falling stocks. PH buys on confirmed upward moves. The direction matters.",
                topics: [
                  "Buying on weakness vs buying on strength — same instrument, opposite signal",
                  "Assignment risk in CSPs and what it does to your cost basis",
                  "How PH uses the CSP as a monthly income stream, not an entry signal",
                ],
                videoSrc: "/tutorials/pyramid_hedging/c02-csp.mp4",
              },
              {
                id: "C03", title: "DCA / Averaging vs. Pyramid Hedging", duration: "90s", priority: "P1",
                desc: "DCA deploys capital on a schedule. PH deploys capital when the market proves momentum. Same goal, opposite signal.",
                topics: [
                  "Time-based vs price-confirmation deployment — why the signal changes risk",
                  "The averaging-down trap: why deploying on weakness feels safe but isn't",
                  "How momentum confirmation changes the probability of each buy",
                ],
                videoSrc: "/tutorials/pyramid_hedging/c03-dca.mp4",
              },
              {
                id: "C04", title: "Wheel Strategy vs. Pyramid Hedging", duration: "105s", priority: "P1",
                desc: "The Wheel is income-first. PH is accumulation-first with income as a byproduct. Two different objectives.",
                topics: [
                  "The Wheel's assignment cycle and why it relies on the stock falling",
                  "Why PH doesn't need assignment — it accumulates on upward moves",
                  "Income generation without needing the stock to drop first",
                ],
                videoSrc: "/tutorials/pyramid_hedging/c04-wheel.mp4",
              },
              {
                id: "C05", title: "Short Strangles & Iron Condors vs. Pyramid Hedging", duration: "2:37 min", priority: "P1",
                desc: "Strangles and iron condors collect the biggest premiums in options trading — and carry open-ended risk on both sides. This video introduces how Pyramid Hedging with CSP and Income-First Mode pursues the same income with a pre-defined profit floor.",
                topics: [
                  "Why both sides of a strangle carry open-ended risk — and why iron condor wings don't fully fix it",
                  "The active-management burden: delta watching, ITM response, two expiry clocks",
                  "Income-First Mode's profit floor: the Minimum Cycle Profit Target",
                  "Automated delta-hedge roll-down keeps the position balanced as the stock moves",
                ],
                videoSrc: "/tutorials/pyramid_hedging/c05-strangles-iron-condors.mp4",
              },
            ],
          },
          {
            series: "Trading Examples",
            color: "#7c3aed",
            icon: "🧪",
            description: "Real accounts, real numbers — live-verified before/after comparisons for the settings that matter most.",
            items: [
              {
                id: "TE01", title: "Trading Example: Cash-Secured Put Disabled", duration: "~2 min", priority: "P1",
                desc: "The simplest version of Pyramid Hedging: stock and a call, no put leg. A real NVDA account shows what call-only income looks like — and why it barely clears a profit when the ladder's average cost drifts above the strike.",
                topics: [
                  "Call-only structure: two legs instead of three, no CSP buying-power reserved",
                  "Real NVDA numbers: $222.50 call strike, ~$276/month premium, Reference Avg Price $225.09",
                  "Why Tail P&L lands at just $17 — the stock leg's small loss almost offsets the call premium",
                  "When call-only is the right choice: simplicity, or limited buying power",
                ],
                videoSrc: "/tutorials/pyramid_hedging/te01-csp-disabled.mp4",
              },
              {
                id: "TE02", title: "Trading Example: Cash-Secured Put Enabled", duration: "~2 min", priority: "P1",
                desc: "Same NVDA account, same call — flip on the Cash-Secured Put and total profit at completion more than doubles. A real before/after comparison showing exactly where the extra income comes from.",
                topics: [
                  "CSP Safety Buffer and CSP Rollup Trigger Distance settings explained with real values",
                  "Real before/after: Tail P&L $198.92 (call only) → $453.79 (call + CSP)",
                  "Why the entire uplift equals the put's own premium — nothing else in the structure changes",
                  "Buying-power check: strike × 100 × lots reserved as collateral before enabling",
                ],
                videoSrc: "/tutorials/pyramid_hedging/te02-csp-enabled.mp4",
              },
              {
                id: "TE03", title: "Trading Example: Income-First Mode OFF (The Default)", duration: "~2 min", priority: "P1",
                desc: "Every Pyramid Hedging strategy starts here. With Income-First Mode off, only one setting steers the ladder — Hedge Style — and there's no profit target doing any work. The baseline every other Trading Example compares back to.",
                topics: [
                  "Hedge Style (average buy price ÷ call strike) is the only thing governing gap width in this mode",
                  "Minimum Ladder Step Gap is a floor, not the answer — Hedge Style can and does widen it further",
                  "Real NVDA numbers: ~1.5% gap, $222.50 call strike, Tail P&L just $17 — trivial by design",
                  "Why this mode was never asked to guarantee a profit number, only to respect the ratio",
                ],
                videoSrc: "/tutorials/pyramid_hedging/te03-income-first-mode-off.mp4",
              },
              {
                id: "TE04", title: "Trading Example: Income-First Mode ON", duration: "~2 min", priority: "P1",
                desc: "Same NVDA account, one toggle flipped: the optimizer stops asking how wide Hedge Style allows the gap to go, and starts asking how little capital it needs to guarantee your profit floor.",
                topics: [
                  "Minimum Cycle Profit Target: the real number this mode is built to defend",
                  "Selection order: fewest lots wins first, then the widest gap that still protects the floor",
                  "Real numbers: $230 call strike, gap narrows to ~1.25%, Tail P&L $1,022 — clearing the $1,000 target by the smallest safe margin, not the biggest",
                  "Why ON doesn't always mean a wider gap — it depends which constraint binds tighter",
                ],
                videoSrc: "/tutorials/pyramid_hedging/te04-income-first-mode-on.mp4",
              },
            ],
          },
          {
            series: "Backtests",
            color: "#7c3aed",
            icon: "📊",
            description: "Real historical price data, replayed day by day, with the actual Adjustments Log and P&L behind every claim.",
            items: [
              {
                id: "BT01", title: "Backtest: Pyramid Hedging — MSFT One Month", duration: "~2:25 min", priority: "P1",
                desc: "A real one-month Pyramid Hedging backtest on MSFT, replayed from the live Backtest page and the same Adjustments Log you'd see on your own account — every rung, the roll-down, the earnings-gap close, and the resulting P&L, one row at a time.",
                topics: [
                  "The real Backtest page — pick a symbol, strategy, and date range, Auto-Run replays the full period",
                  "Day one: the strangle opens immediately, call and CSP sold before a single share is bought",
                  "The ladder builds through July, then the hedge gets rolled down as the call decays to near-zero",
                  "An earnings gap sends MSFT up 14% in one session — the calls close out profitable, the stock position gets trimmed into strength",
                  "Final tally read straight from the Adjustments Log and P&L card: 27 days, 14 adjustments, total P&L",
                ],
                videoSrc: "/tutorials/pyramid_hedging/bt01_msft_one_month_backtest.mp4",
              },
              {
                id: "BT02", title: "Backtest: Pyramid Hedging — MSFT, The Crash", duration: "~4 min", priority: "P1",
                desc: "A second real one-month Pyramid Hedging backtest on MSFT — this time a sharp mid-month decline, showing how the hedge defends itself through repeated expiry rolls as the stock keeps falling.",
                topics: [
                  "A ladder built during a rally, then tested by a real ~16% drop from the peak",
                  "The call hedge rolled out to fresh expiries multiple times as each near-worthless contract decayed",
                  "Running P&L: premium collected vs unrealised stock and option P&L through the decline",
                  "End-of-month summary: total adjustments made and net P&L despite the stock finishing underwater",
                ],
                videoSrc: "/tutorials/pyramid_hedging/bt02_msft_one_month_backtest.mp4",
              },
              {
                id: "BT03", title: "Backtest: Pyramid Hedging — AAPL, Same Strategy, Six Settings", duration: "~2:45 min", priority: "P1",
                desc: "Same stock, same six-week window, same starting capital — this backtest holds the price path fixed and changes one Pyramid Hedging setting at a time across six runs, isolating what each parameter is actually worth in real dollars.",
                topics: [
                  "Six real backtest runs on AAPL, April 14 – June 19, one setting varied per run",
                  "Companion to the Settings Guide: that video explains what each setting does, this one proves what it's worth",
                  "One toggle was the difference between losing $1,523 and making $710",
                  "All numbers verified against real backtest_jobs settings — no seeded or mock data",
                ],
                videoSrc: "/tutorials/pyramid_hedging/bt03_aapl_parameter_sweep.mp4",
              },
            ],
          },
        ],
      },

      /* ══════════════════════ FUTURES PYRAMID HEDGING ══════════════════════ */
      {
        series: "Futures Pyramid Hedging",
        color: "#0891b2",
        icon: "🪜",
        description: "The next strategy on the platform — extends Pyramid Hedging's accumulation ladder to index and single-stock futures. Build a fully-hedged futures position one contract at a time, with a configurable per-future hedge ratio (calls_per_future, which can be more than one call), and two independent expiry cycles managed automatically: the weekly call hedge and the futures contract's own monthly rollover.",
        children: [
          {
            series: "Strategy Guide",
            color: "#0891b2",
            icon: "📘",
            description: "How Futures Pyramid Hedging works, how it compares to the stock version, and every setting explained.",
            items: [
              {
                id: "FPH-I", title: "Introduction to Futures Pyramid Hedging", duration: "~3 min", priority: "P0",
                desc: "Futures Pyramid Hedging takes the same momentum-confirmed accumulation ladder from Pyramid Hedging and applies it to index or single-stock futures — building toward a fully covered position, hedged entirely by short weekly calls.",
                topics: [
                  "The hedge ratio: calls_per_future short call lots back every futures contract — and it can be more than one call",
                  "Why each ladder rung is exactly one futures contract — no capital search, just confirmed momentum",
                  "Calls-only hedge: no CSP/put leg, unlike Pyramid Hedging's stock variant",
                  "Two independent expiry cycles: the weekly call hedge rolls separately from the futures contract's own monthly expiry",
                ],
                videoSrc: "/tutorials/futures_pyramid_hedging/fph-intro.mp4",
              },
              {
                id: "FPH-SYN", title: "Synthetic Stock: The Alternative to Futures", duration: "~3 min", priority: "P0",
                desc: "Most individual US stocks have no listed futures contract. This video explains how Futures Pyramid Hedging replicates the ladder's underlying leg with a same-strike long call + short put instead — using put-call parity — and how that combo is rolled and defended differently from a real futures contract.",
                topics: [
                  "Put-call parity: why a long call minus a short put at the same strike moves like the stock itself",
                  "Why the common strike sits slightly below spot — keeping assignment risk on the OTM short put, not the ITM long call",
                  "Two different calls: the long call inside the synthetic combo vs. the short call hedge (calls_per_future) sold on top of it",
                  "Rolling short-dated options vs. a real futures contract's monthly cycle — the backstop and defensive triggers behind ROLL_SYNTHETIC_STOCK",
                ],
                videoSrc: null,
              },
              {
                id: "C07", title: "Futures Pyramid Hedging vs. Pyramid Hedging", duration: "~2 min", priority: "P1",
                desc: "Same accumulation philosophy, different instrument. This video breaks down exactly what changes when the ladder is built on futures contracts instead of cash equity shares — leverage, lot-sized rungs, and a second expiry to manage.",
                topics: [
                  "Equity shares vs. futures contracts: leverage, margin, and lot-quantized rungs",
                  "Why the futures ladder drops the CSP leg and runs calls-only",
                  "A fixed per-future hedge ratio (calls_per_future, can be > 1) vs. Pyramid Hedging's call + CSP hedge",
                  "Managing two expiries: the weekly call hedge and the monthly futures rollover",
                ],
                videoSrc: null,
              },
              {
                id: "C08", title: "Index Futures vs. Single-Stock Futures", duration: "~2 min", priority: "P1",
                desc: "Futures Pyramid Hedging works on index futures and single-stock futures alike. This video covers how to choose between them — lot sizes, liquidity, and what stays the same regardless of which you pick.",
                topics: [
                  "Index futures: broad market exposure, standardized contract specs",
                  "Single-stock futures: concentrated exposure to a name you already follow, smaller notional per lot",
                  "Lot size and margin differences — and why the ladder math adapts automatically",
                  "Choosing the right underlying for your account size and conviction",
                ],
                videoSrc: null,
              },
              {
                id: "FPH-S", title: "Futures Pyramid Hedging: Settings Guide", duration: "~7 min", priority: "P1",
                desc: "A complete walkthrough of every Futures Pyramid Hedging setting, organized as 3 chapters. By the end you'll understand the calls_per_future hedge ratio, the ladder spacing and length search, Cash Saving Mode, and how the two expiry cycles are managed independently.",
                topics: [
                  "Chapter 1 — The Hedge Ratio: calls_per_future (can be more than one call per future), Min/Max Hedge %, and how it's derived from the delta scan",
                  "Chapter 2 — Ladder Spacing & Cash Saving Mode: minimum step gap, Min Profit CSM, and the tail-P&L search",
                  "Chapter 3 — Two Expiries: weekly call rolls (down/out/up) vs. the futures contract's own monthly rollover",
                ],
                videoSrc: "/tutorials/futures_pyramid_hedging/fph-settings-guide.mp4",
              },
            ],
          },
          {
            series: "Trading Examples",
            color: "#0891b2",
            icon: "🧪",
            description: "Real futures accounts, before/after comparisons for the settings that matter most.",
            items: [
              {
                id: "TE-FPH01", title: "Trading Example: Futures Pyramid Hedging — Coming Soon", duration: "~2 min", priority: "P2",
                desc: "A real futures account showing how one Futures Pyramid Hedging setting changes the outcome — live-verified before/after numbers, same format as the Pyramid Hedging Trading Examples.",
                topics: [
                  "Setting TBD: likely calls_per_future (hedge ratio) or Cash Saving Mode, mirroring the stock-strategy examples",
                  "Same account, same futures contract, one toggle changed",
                  "Real before/after numbers — no seeded or hypothetical data",
                ],
                videoSrc: null,
              },
            ],
          },
          {
            series: "Backtests",
            color: "#0891b2",
            icon: "📊",
            description: "Real historical price data, every ladder rung and call hedge roll replayed.",
            items: [
              {
                id: "BT-FPH01", title: "Backtest: Futures Pyramid Hedging — Coming Soon", duration: "~2 min", priority: "P2",
                desc: "A real Futures Pyramid Hedging backtest on an index or single-stock futures contract — every ladder rung added, every call hedge roll, and the futures contract's own monthly rollover, replayed from historical data.",
                topics: [
                  "Underlying TBD: index futures (e.g., NQ or ES) or a single-stock futures contract",
                  "Ladder rung events: each futures contract added on confirmed momentum, hedge placed at calls_per_future ratio",
                  "Call hedge rolls: weekly roll events showing down/out/up path taken and premium collected",
                  "Futures rollover: the monthly futures contract roll and how it affects the ladder accounting",
                  "End-of-period summary: total premium collected, unrealised futures P&L, and net position",
                ],
                videoSrc: null,
              },
            ],
          },
        ],
      },

      /* ══════════════════════ WEEKLY INDEX SHORT STRADDLE ══════════════════════ */
      {
        series: "Weekly Index Short Straddle",
        color: "#f59e0b",
        icon: "🎯",
        description: "The Weekly Index Short Straddle opens an ATM straddle on SPX every Monday, holds it through the week, and closes before Friday expiry. One position per week — managed by standard rolls (±5 SPX points), recovery rolls (when net P&L drops below your floor), and an upper profit target that can trigger an early exit and re-entry.",
        children: [
          {
            series: "Strategy Guide",
            color: "#f59e0b",
            icon: "📘",
            description: "How WISS works and every setting explained.",
            items: [
              {
                id: "WISS-I", title: "Weekly Index Short Straddle: Introduction", duration: "~3 min", priority: "P0",
                desc: "How WISS works: selling an ATM SPX call and put every Monday, holding through the week, rolling legs as the index moves, and closing before Friday settlement. The SPX weekly premium income strategy — European-style, cash-settled, no assignment risk.",
                topics: [
                  "SPX weekly options: European-style, cash-settled, Friday expiry — closest US equivalent to India's NIFTY weekly straddle",
                  "Monday entry at 09:35 ET: ATM strike found automatically on the $5 SPX grid; call + put sold at the same strike, same expiry",
                  "Weekly hold: the straddle stays open Mon–Thu overnight — V2 has no daily EOD close",
                  "Standard roll (ROLL_LIGHTER_SIDE): fires when the OTM leg's strike is ±5 SPX points from current price; profit gate + extrinsic gate both required",
                  "Recovery roll: fires when net potential profit drops below min_profit floor; identifies the ITM leg (low extrinsic ratio) and repositions it via min-profit-restore or symmetric mode",
                  "Two exits: upper_target ($1,000 default) closes early on any day; CLOSE_ON_EXPIRY_DAY fires Friday at 15:45 ET",
                ],
                videoSrc: null,
              },
              {
                id: "WISS-S", title: "Weekly Index Short Straddle: Settings Guide", duration: "~5 min", priority: "P1",
                desc: "A complete walkthrough of every WISS setting, organized as 3 chapters. By the end you'll understand the Monday open window, the Friday-only expiry close, SPX's $5 strike grid and contract sizing, and the three risk mechanisms: standard roll, upper target, and recovery roll.",
                topics: [
                  "Chapter 1 — Timing: open_time (09:35 ET, when OPEN_WEEKLY_STRADDLE queues) and expiry_close_time (15:45 ET, fires Friday only — not daily)",
                  "Chapter 2 — Sizing: ATM strike on the $5 SPX grid (auto-computed at open), lots per side (default 1), nearest Friday expiry (auto-resolved)",
                  "Chapter 3 — Risk Management: standard roll boundary (±5 SPX points from OTM strike, min_roll_profit_per_lot gate), upper_target early-close trigger, and recovery roll (min_profit floor, two repositioning modes)",
                ],
                videoSrc: null,
              },
            ],
          },
          {
            series: "Trading Examples",
            color: "#f59e0b",
            icon: "🧪",
            description: "Real SPX accounts, before/after comparisons for the settings that matter most.",
            items: [
              {
                id: "TE-WISS01", title: "Trading Example: Weekly Index Short Straddle — Coming Soon", duration: "~2 min", priority: "P2",
                desc: "A real SPX account showing how one WISS setting changes the outcome — live-verified before/after numbers, same format as the Pyramid Hedging Trading Examples.",
                topics: [
                  "Setting TBD: likely the standard roll boundary or upper_target, mirroring the stock-strategy examples",
                  "Same account, same week, one toggle changed",
                  "Real before/after numbers — no seeded or hypothetical data",
                ],
                videoSrc: null,
              },
            ],
          },
          {
            series: "Backtests",
            color: "#f59e0b",
            icon: "📊",
            description: "Real historical SPX price data, one week fully replayed.",
            items: [
              {
                id: "BT-WISS01", title: "Backtest: Weekly Index Short Straddle — Coming Soon", duration: "~3 min", priority: "P2",
                desc: "A real WISS backtest on SPX — ATM straddle opened Monday, at least one standard roll as SPX moves ±5 points, and a Friday expiry close. One week of premium income replayed from real historical price data.",
                topics: [
                  "Week selected: ideally SPX moves 10–15 points in one direction mid-week (triggering one standard roll), then stabilizes",
                  "Monday 09:35 ET entry: ATM strike on the $5 grid, combined straddle premium collected",
                  "Roll event: which side crossed the ±5 boundary, the closing cost and re-opening premium, net roll cost",
                  "Friday 15:45 ET expiry close: final buyback cost for both legs, net weekly P&L",
                  "Return in $ per lot — SPX contract at 100× multiplier",
                ],
                videoSrc: null,
              },
            ],
          },
        ],
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * SECTION 6 — ACCOUNT SETTINGS
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Account Settings",
    color: "#d92550",
    icon: "🔧",
    description: "Global account configuration: delta thresholds, broker connection, notification channels, and subscription plan limits.",
    items: [
      {
        id: "09", title: "Configuring Your Account", duration: "2 min", priority: "P1",
        desc: "A walkthrough of all global account settings — the configuration that applies across all your strategies, not just individual ones.",
        topics: [
          "Global delta thresholds: the account-level bounds that all strategies inherit",
          "Adjustment type: how to set whether the system suggests or automatically adjusts",
          "Broker account setup: connecting Tradier, verifying credentials, and testing connectivity",
          "Telegram and email notifications: enabling alerts and approval reminders",
          "Subscription plan limits: max strategies, max accounts, and cycle frequency",
        ],
        videoSrc: "/tutorials/platform/09-settings.mp4",
      },
    ],
  },
];

/* ── generic node helpers (works at any depth) ────────────────────────────── */
function nodeItems(node) {
  if (node.items) return node.items;
  if (node.children) return node.children.flatMap(nodeItems);
  return [];
}

/* lookup: tutorialId → tutorial object (series = its immediate parent's name) */
export function getTutorialById(id) {
  function walk(nodes) {
    for (const node of nodes) {
      if (node.items) {
        const found = node.items.find(t => t.id === id);
        if (found) return { ...found, series: node.series };
      } else if (node.children) {
        const found = walk(node.children);
        if (found) return found;
      }
    }
    return null;
  }
  return walk(TUTORIALS);
}

/* lookup: route path → best tutorial id */
export function getTutorialForRoute(path) {
  const item = nodeItems({ children: TUTORIALS }).find(i => i.routes?.includes(path));
  return item ? item.id : null;
}

/* ── Component ──────────────────────────────────────────────────────────── */

// Chain of series names (root → leaf) that must all be open to reveal `id`.
function openPathForId(id) {
  if (!id) return [];
  function walk(nodes, path) {
    for (const node of nodes) {
      const p = [...path, node.series];
      if (node.items) {
        if (node.items.some(i => i.id === id)) return p;
      } else if (node.children) {
        const found = walk(node.children, p);
        if (found) return found;
      }
    }
    return null;
  }
  return walk(TUTORIALS, []) || [];
}

function SectionHeader({ node, index, isOpen, onToggle, depth }) {
  const items = nodeItems(node);
  const readyCount = items.filter(i => i.videoSrc).length;
  const nested = depth > 0;
  return (
    <div
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
      style={{
        display: "flex", alignItems: "flex-start", gap: nested ? 10 : 12,
        marginBottom: isOpen ? (nested ? 12 : 16) : 0, paddingBottom: nested ? 8 : 12,
        borderBottom: `2px solid ${node.color}33`,
        cursor: "pointer", userSelect: "none",
      }}
    >
      <div style={{
        width: nested ? 28 : 36, height: nested ? 28 : 36, borderRadius: nested ? 8 : 10, flexShrink: 0,
        background: `linear-gradient(135deg, ${node.color}22, ${node.color}44)`,
        border: `1px solid ${node.color}33`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: nested ? 13 : 18,
      }}>
        {node.icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: node.description ? 4 : 0, flexWrap: "wrap" }}>
          <span style={{
            fontSize: nested ? 10 : 12, color: "#999", flexShrink: 0,
            display: "inline-block", transition: "transform 0.15s",
            transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
          }}>
            ▶
          </span>
          <span style={{ fontSize: depth === 0 ? 15 : depth === 1 ? 13.5 : 12.5, fontWeight: 700, color: "#222" }}>
            {index != null ? `${index}. ` : ""}{node.series}
          </span>
          <span style={{
            fontSize: 11, color: node.color, fontWeight: 600,
            background: `${node.color}15`, borderRadius: 20, padding: "1px 8px",
            border: `1px solid ${node.color}33`,
          }}>
            {items.length} video{items.length !== 1 ? "s" : ""}
          </span>
          <span style={{
            fontSize: 11, color: "#aaa", fontWeight: 500,
            background: "#f5f5f5", borderRadius: 20, padding: "1px 8px",
            border: "1px solid #e8e8e8",
          }}>
            {readyCount} ready
          </span>
        </div>
        {node.description && (
          <p style={{ margin: 0, fontSize: nested ? 11.5 : 12, color: "#777", lineHeight: 1.5 }}>
            {node.description}
          </p>
        )}
      </div>
    </div>
  );
}

function TutorialCard({ item, node, highlightId, highlightRef, onPlay }) {
  const isHighlighted = item.id === highlightId;
  const isReady = Boolean(item.videoSrc);
  return (
    <div
      key={item.id}
      ref={isHighlighted ? highlightRef : null}
      style={{
        display: "flex", alignItems: "flex-start", gap: 14,
        background: isHighlighted ? `${node.color}0d` : "#fff",
        border: `1px solid ${isHighlighted ? node.color : "#eee"}`,
        borderLeft: `4px solid ${isHighlighted ? node.color : isReady ? node.color + "66" : "#ddd"}`,
        borderRadius: 10,
        padding: "12px 16px",
        opacity: isReady ? 1 : 0.78,
        transition: "border-color 0.2s, background 0.2s, opacity 0.2s",
        boxShadow: isHighlighted ? `0 2px 12px ${node.color}22` : "0 1px 3px rgba(0,0,0,0.04)",
      }}
      onMouseEnter={e => {
        if (!isHighlighted && isReady) {
          e.currentTarget.style.borderColor = `${node.color}66`;
          e.currentTarget.style.background = "#fafafa";
        }
      }}
      onMouseLeave={e => {
        if (!isHighlighted) {
          e.currentTarget.style.borderColor = isReady ? `${node.color}66` : "#ddd";
          e.currentTarget.style.background = "#fff";
        }
      }}
    >
      {/* step number + thumbnail */}
      <div style={{
        width: 56, height: 44, borderRadius: 8, flexShrink: 0,
        background: isReady
          ? `linear-gradient(135deg, ${node.color}22 0%, ${node.color}44 100%)`
          : "linear-gradient(135deg, #f0f0f0, #e0e0e0)",
        border: `1px solid ${isReady ? node.color + "33" : "#ddd"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative", overflow: "hidden",
        fontSize: 18,
      }}>
        {isReady ? node.icon : "🔜"}
        <span style={{
          position: "absolute", bottom: 2, right: 3,
          fontSize: 9, fontWeight: 700,
          color: isReady ? node.color : "#aaa",
          background: "#fff", borderRadius: 3, padding: "0 3px",
        }}>
          {item.id}
        </span>
      </div>

      {/* text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: isReady ? "#222" : "#666" }}>
            {item.title}
          </span>
          {isHighlighted && (
            <span style={{
              fontSize: 9, background: node.color, color: "#fff",
              borderRadius: 10, padding: "1px 6px", fontWeight: 700,
            }}>
              SUGGESTED
            </span>
          )}
          {!isReady && (
            <span style={{
              fontSize: 9, background: "#f0f0f0", color: "#888",
              borderRadius: 10, padding: "1px 6px", fontWeight: 600,
              border: "1px solid #ddd",
            }}>
              COMING SOON
            </span>
          )}
        </div>
        <div style={{
          fontSize: 12, color: "#777", lineHeight: 1.45, marginBottom: !isReady && item.topics?.length ? 6 : 0,
          overflow: isReady ? "hidden" : "visible",
          textOverflow: isReady ? "ellipsis" : "unset",
          display: isReady ? "-webkit-box" : "block",
          WebkitLineClamp: isReady ? 2 : undefined,
          WebkitBoxOrient: isReady ? "vertical" : undefined,
        }}>
          {item.desc}
        </div>
        {/* Coming soon: show full topic list so users know what's coming */}
        {!isReady && item.topics?.length > 0 && (
          <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
            {item.topics.map((t, i) => (
              <li key={i} style={{ fontSize: 11, color: "#888", marginBottom: 2, lineHeight: 1.45 }}>
                {t}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* badges + action */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 4 }}>
          <span style={{
            fontSize: 10, background: `${node.color}15`, color: isReady ? node.color : "#aaa",
            border: `1px solid ${isReady ? node.color + "33" : "#ddd"}`,
            borderRadius: 20, padding: "1px 7px", fontWeight: 600,
          }}>
            {item.duration}
          </span>
          {item.priority === "P0" && (
            <span style={{
              fontSize: 10, background: "#fff3cd", color: "#856404",
              border: "1px solid #ffc107", borderRadius: 20, padding: "1px 7px", fontWeight: 600,
            }}>
              Essential
            </span>
          )}
        </div>
        <button
          onClick={() => isReady && onPlay({ ...item, series: node.series })}
          disabled={!isReady}
          style={{
            background: isReady ? node.color : "#e0e0e0",
            color: isReady ? "#fff" : "#aaa",
            border: "none", borderRadius: 6,
            fontSize: 11, fontWeight: 600,
            padding: "5px 14px",
            cursor: isReady ? "pointer" : "not-allowed",
            whiteSpace: "nowrap",
            opacity: isReady ? 1 : 0.7,
          }}
          onMouseEnter={e => { if (isReady) e.currentTarget.style.opacity = "0.85"; }}
          onMouseLeave={e => { if (isReady) e.currentTarget.style.opacity = "1"; }}
        >
          {isReady ? "▶ Watch" : "Coming Soon"}
        </button>
      </div>
    </div>
  );
}

// Recursive: renders a node's header, and (if open) either its child nodes
// or its leaf tutorial cards.
function TreeNode({ node, index, depth, openSections, toggleSection, highlightId, highlightRef, onPlay }) {
  const isOpen = openSections.has(node.series);
  return (
    <div style={{
      marginBottom: isOpen ? (depth === 0 ? 40 : 20) : (depth === 0 ? 12 : 4),
      marginLeft: depth > 0 ? 20 : 0,
    }}>
      <SectionHeader
        node={node}
        index={index}
        isOpen={isOpen}
        onToggle={() => toggleSection(node.series)}
        depth={depth}
      />

      {isOpen && node.children && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {node.children.map(child => (
            <TreeNode
              key={child.series}
              node={child}
              index={null}
              depth={depth + 1}
              openSections={openSections}
              toggleSection={toggleSection}
              highlightId={highlightId}
              highlightRef={highlightRef}
              onPlay={onPlay}
            />
          ))}
        </div>
      )}

      {isOpen && node.items && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: depth > 0 ? 12 : 0 }}>
          {node.items.map(item => (
            <TutorialCard
              key={item.id}
              item={item}
              node={node}
              highlightId={highlightId}
              highlightRef={highlightRef}
              onPlay={onPlay}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TutorialTree({ highlightId, onPlay }) {
  const highlightRef = useRef(null);

  // Accordion: default-collapsed, except the section(s) containing the current/highlighted video.
  const [openSections, setOpenSections] = useState(() => new Set(openPathForId(highlightId)));

  useEffect(() => {
    const path = openPathForId(highlightId);
    if (!path.length) return;
    setOpenSections(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const s of path) if (!next.has(s)) { next.add(s); changed = true; }
      return changed ? next : prev;
    });
  }, [highlightId]);

  useEffect(() => {
    if (highlightRef.current) {
      setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
    }
  }, [highlightId]);

  const toggleSection = (series) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(series)) next.delete(series);
      else next.add(series);
      return next;
    });
  };

  const allItems = nodeItems({ children: TUTORIALS });
  const totalVideos = allItems.length;
  const readyVideos = allItems.filter(i => i.videoSrc).length;

  return (
    <div style={{ padding: "24px 28px", maxWidth: 900, margin: "0 auto" }}>

      {/* page header */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 800, color: "#1a1a2e" }}>
          Video Library
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: "#888" }}>
          {readyVideos} videos available · {totalVideos - readyVideos} coming soon · Watch in order for the best learning path
        </p>
      </div>

      {TUTORIALS.map((node, idx) => (
        <TreeNode
          key={node.series}
          node={node}
          index={idx + 1}
          depth={0}
          openSections={openSections}
          toggleSection={toggleSection}
          highlightId={highlightId}
          highlightRef={highlightRef}
          onPlay={onPlay}
        />
      ))}
    </div>
  );
}
